/**
 * Container Notes Tab Component
 *
 * Displays notes for a container in a tab panel format.
 * Features:
 * - Three sections: Internal Notes | Customer Notes | Actions
 * - List existing notes in each section
 * - Form to add new notes (internal / customer)
 * - Delete action per note
 * - Actions section: shows issue action history + document evaluation entries
 */

import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/base/buttons/button';
import { TextArea } from '@/components/base/textarea/textarea';
import { Badge } from '@/components/base/badges/badges';
import { toast } from '@/lib/toast-service';
import {
    useContainerNotes,
    useCreateContainerNote,
    useDeleteContainerNote,
    type ContainerNote,
} from '@/hooks/api/use-container-notes';
import { useContainerActivities } from '@/hooks/api/use-activities';
import type { BackendActivity } from '@/data/api/activities';
import {
    EvaluateDocumentsModal,
    type EvaluationResults,
} from './modals/evaluate-documents-modal';
import type {
    SingleCheckResult,
    CrossCheckRunResult,
    AICrossAnalysisResult,
    AICrossAnalysisFinding,
} from '@/hooks/api/use-container-validation';
import {
    Plus,
    Trash01,
    Lock01,
    Users01,
    MessageTextSquare01,
    CheckCircle,
    Zap,
    Eye,
} from '@untitledui/icons';

interface ContainerNotesTabProps {
    containerId: string;
    containerNumber: string;
}

type TabType = 'internal' | 'customer' | 'actions';

// ============================================================================
// Action status config
// ============================================================================

const ACTION_STATUS_COLORS: Record<string, 'success' | 'brand' | 'gray' | 'warning' | 'error'> = {
    acknowledged: 'brand',
    resolved: 'success',
    false_positive: 'gray',
    wont_fix: 'gray',
    in_progress: 'warning',
    pending_info: 'warning',
    open: 'error',
};

const ACTION_STATUS_KEYS: Record<string, string> = {
    acknowledged: 'detail.notes.acknowledged',
    resolved: 'detail.notes.resolved',
    false_positive: 'detail.notes.dismissed',
    wont_fix: 'detail.notes.wontFix',
    in_progress: 'detail.notes.inProgress',
    pending_info: 'detail.notes.pendingInfo',
    open: 'detail.notes.opened',
};

// ============================================================================
// Helpers
// ============================================================================

const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
};

/** Extract stored evaluation results from an activity's details */
function extractEvaluationResults(activity: BackendActivity): EvaluationResults | null {
    const details = activity.details as Record<string, unknown> | null;
    if (!details?.evaluation_results) return null;

    const evalResults = details.evaluation_results as Record<string, unknown>;

    const singleCheck = (evalResults.single_check as SingleCheckResult) ?? null;
    const crossCheck = (evalResults.cross_check as CrossCheckRunResult) ?? null;

    let aiAnalysis: AICrossAnalysisResult | null = null;
    const aiData = evalResults.ai_analysis as Record<string, unknown> | null;
    if (aiData) {
        aiAnalysis = {
            summary: (aiData.summary as string) ?? '',
            findings: ((aiData.findings as Array<Record<string, unknown>>) ?? []).map(f => ({
                severity: (f.severity as string) ?? 'low',
                category: (f.category as string) ?? 'other',
                description: (f.description as string) ?? '',
                documents_involved: (f.documents_involved as string[]) ?? [],
            })) as AICrossAnalysisFinding[],
            documents_analyzed: (details.documents_analyzed as number) ?? 0,
            issues_created: (details.issues_created as number) ?? 0,
        };
    }

    return { singleCheck, crossCheck, aiAnalysis };
}

// ============================================================================
// Sub-components
// ============================================================================

const NoteItem = ({
    note,
    onDelete,
    isDeleting,
}: {
    note: ContainerNote;
    onDelete: (noteId: string) => void;
    isDeleting: boolean;
}) => {
    const { t } = useTranslation('pages:containers');
    return (
        <div className="p-4 rounded-lg bg-secondary/30 ring-1 ring-secondary group">
            <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                    <p className="text-sm text-primary whitespace-pre-wrap">{note.content}</p>
                    <div className="flex items-center gap-2 mt-2 text-xs text-tertiary">
                        <span>{note.created_by_name || 'Unknown'}</span>
                        <span>-</span>
                        <span>{formatDate(note.created_at)}</span>
                        {note.updated_at && (
                            <>
                                <span>-</span>
                                <span className="italic">{t('detail.notes.edited')}</span>
                            </>
                        )}
                    </div>
                </div>
                <button
                    onClick={() => onDelete(note.note_id)}
                    disabled={isDeleting}
                    className="opacity-0 group-hover:opacity-100 p-1.5 rounded-lg text-tertiary hover:text-error-600 hover:bg-error-50 transition-all"
                    title="Delete note"
                >
                    <Trash01 className="h-4 w-4" />
                </button>
            </div>
        </div>
    );
};

const EmptyState = ({ type }: { type: TabType }) => {
    const { t } = useTranslation('pages:containers');

    if (type === 'actions') {
        return (
            <div className="flex flex-col items-center justify-center py-8 text-center">
                <div className="rounded-full bg-secondary/50 p-3 mb-3">
                    <CheckCircle className="h-6 w-6 text-tertiary" />
                </div>
                <p className="text-sm text-tertiary">{t('detail.notes.noActionsYet')}</p>
                <p className="text-xs text-tertiary mt-1">
                    {t('detail.notes.noActionsDesc')}
                </p>
            </div>
        );
    }

    const Icon = type === 'internal' ? Lock01 : Users01;
    return (
        <div className="flex flex-col items-center justify-center py-8 text-center">
            <div className="rounded-full bg-secondary/50 p-3 mb-3">
                <Icon className="h-6 w-6 text-tertiary" />
            </div>
            <p className="text-sm text-tertiary">
                {type === 'internal'
                    ? t('detail.actions.noInternalNotes', 'No internal notes yet')
                    : t('detail.actions.noCustomerNotes', 'No customer notes yet')}
            </p>
            <p className="text-xs text-tertiary mt-1">
                {type === 'internal'
                    ? t('detail.actions.internalNoteDesc', 'Internal notes are only visible to your team')
                    : t('detail.actions.customerNoteDesc', 'Customer notes are visible to all stakeholders')}
            </p>
        </div>
    );
};

/** Single issue action item in the Actions tab */
const ActionItem = ({ activity }: { activity: BackendActivity }) => {
    const { t } = useTranslation('pages:containers');
    const details = activity.details as Record<string, string> | null;
    const newStatus = details?.new_status ?? '';
    const oldStatus = details?.old_status ?? '';
    const reason = details?.reason;
    const issueTitle = details?.issue_title ?? '';
    const issueType = details?.issue_type_display ?? '';
    const changedBy = details?.changed_by ?? activity.actor_name;

    const statusLabel = ACTION_STATUS_KEYS[newStatus] ? t(ACTION_STATUS_KEYS[newStatus]) : newStatus.replace(/_/g, ' ');
    const statusColor = ACTION_STATUS_COLORS[newStatus] ?? 'gray' as const;
    const oldStatusLabel = ACTION_STATUS_KEYS[oldStatus] ? t(ACTION_STATUS_KEYS[oldStatus]) : undefined;
    const oldStatusColor = ACTION_STATUS_COLORS[oldStatus];

    return (
        <div className="p-4 rounded-lg bg-secondary/30 ring-1 ring-secondary">
            {/* Header: issue title + status badge */}
            <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-primary">{issueTitle}</p>
                    {issueType && (
                        <p className="text-xs text-tertiary mt-0.5">{issueType}</p>
                    )}
                </div>
                <Badge color={statusColor} size="sm">
                    {statusLabel}
                </Badge>
            </div>

            {/* Status transition */}
            <div className="flex items-center gap-2 mt-2 text-xs text-tertiary">
                {oldStatusLabel && oldStatusColor && (
                    <>
                        <span>{oldStatusLabel}</span>
                        <span>&rarr;</span>
                    </>
                )}
                <span className="font-medium text-secondary">{statusLabel}</span>
            </div>

            {/* Reason/note */}
            {reason && (
                <div className="mt-2 pl-3 border-l-2 border-secondary">
                    <p className="text-sm text-secondary whitespace-pre-wrap">{reason}</p>
                </div>
            )}

            {/* Meta */}
            <div className="flex items-center gap-2 mt-2 text-xs text-tertiary">
                <span>{changedBy}</span>
                <span>-</span>
                <span>{formatDate(activity.created_at)}</span>
            </div>
        </div>
    );
};

/** Evaluation action item — shows doc analysis summary, click to view results */
const EvaluationActionItem = ({
    activity,
    onViewResults,
}: {
    activity: BackendActivity;
    onViewResults: (results: EvaluationResults) => void;
}) => {
    const { t } = useTranslation('pages:containers');
    const details = activity.details as Record<string, unknown> | null;
    const findingsCount = (details?.ai_findings_count as number) ?? 0;
    const issuesCreated = (details?.issues_created as number) ?? 0;
    const triggeredBy = (details?.triggered_by as string) ?? activity.actor_name;
    const storedResults = extractEvaluationResults(activity);

    return (
        <div className="p-4 rounded-lg bg-brand-50 dark:bg-brand-900/20 ring-1 ring-brand-200 dark:ring-brand-800">
            <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2">
                    <Zap className="h-4 w-4 text-brand-600 shrink-0" />
                    <p className="text-sm font-medium text-primary">{t('detail.notes.documentAnalysis')}</p>
                </div>
                <div className="flex items-center gap-2">
                    {findingsCount > 0 && (
                        <Badge color="warning" size="sm">
                            {t('detail.notes.findingsCount', { count: findingsCount })}
                        </Badge>
                    )}
                    {issuesCreated > 0 && (
                        <Badge color="error" size="sm">
                            {t('detail.notes.issuesCount', { count: issuesCreated })}
                        </Badge>
                    )}
                    {findingsCount === 0 && issuesCreated === 0 && (
                        <Badge color="success" size="sm">{t('detail.notes.allClear')}</Badge>
                    )}
                </div>
            </div>

            <div className="flex items-center justify-between mt-3">
                <div className="flex items-center gap-2 text-xs text-tertiary">
                    <span>{triggeredBy}</span>
                    <span>-</span>
                    <span>{formatDate(activity.created_at)}</span>
                </div>
                {storedResults && (
                    <button
                        onClick={() => onViewResults(storedResults)}
                        className="flex items-center gap-1.5 text-xs font-medium text-brand-600 hover:text-brand-700 transition-colors"
                    >
                        <Eye className="h-3.5 w-3.5" />
                        {t('detail.notes.viewResults')}
                    </button>
                )}
            </div>
        </div>
    );
};

// ============================================================================
// Main component
// ============================================================================

export function ContainerNotesTab({ containerId, containerNumber }: ContainerNotesTabProps) {
    const { t } = useTranslation('pages:containers');

    const [activeTab, setActiveTab] = useState<TabType>('internal');
    const [newNoteContent, setNewNoteContent] = useState('');
    const [deletingNoteId, setDeletingNoteId] = useState<string | null>(null);

    // View-mode evaluation results modal
    const [viewResults, setViewResults] = useState<EvaluationResults | null>(null);

    // Fetch notes
    const { data: notesData, isLoading } = useContainerNotes(containerId);

    // Fetch activities for actions tab
    const { data: activitiesData, isLoading: activitiesLoading } = useContainerActivities(containerId);

    // Filter issue status change activities
    const issueActions = useMemo(() => {
        const activities = activitiesData?.activities ?? [];
        return activities
            .filter(
                (a) =>
                    a.entity_type === 'container_issue' &&
                    a.activity_type === 'issue_resolved' &&
                    a.details &&
                    (a.details as Record<string, string>).new_status != null
            )
            .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    }, [activitiesData?.activities]);

    // Filter evaluation activities
    const evaluationActions = useMemo(() => {
        const activities = activitiesData?.activities ?? [];
        return activities
            .filter(a => a.activity_type === 'single_check_executed')
            .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    }, [activitiesData?.activities]);

    const totalActionsCount = issueActions.length + evaluationActions.length;

    // Mutations
    const createMutation = useCreateContainerNote(containerId);
    const deleteMutation = useDeleteContainerNote(containerId);

    const handleAddNote = async () => {
        if (!newNoteContent.trim()) {
            toast.error(t('detail.actions.noteEmpty', 'Note cannot be empty'));
            return;
        }

        const noteType = activeTab === 'customer' ? 'customer' : 'internal';
        try {
            await createMutation.mutateAsync({
                note_type: noteType,
                content: newNoteContent.trim(),
            });
            setNewNoteContent('');
            toast.success(t('detail.actions.noteAdded', 'Note added successfully'));
        } catch {
            toast.error(t('detail.actions.noteError', 'Failed to add note'));
        }
    };

    const handleDeleteNote = async (noteId: string) => {
        setDeletingNoteId(noteId);
        try {
            await deleteMutation.mutateAsync(noteId);
            toast.success(t('detail.actions.noteDeleted', 'Note deleted'));
        } catch {
            toast.error(t('detail.actions.noteDeleteError', 'Failed to delete note'));
        } finally {
            setDeletingNoteId(null);
        }
    };

    const currentNotes = activeTab === 'internal'
        ? notesData?.internal_notes || []
        : activeTab === 'customer'
            ? notesData?.customer_notes || []
            : [];

    const isNotesTab = activeTab === 'internal' || activeTab === 'customer';

    return (
        <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
            {/* Header */}
            <div className="flex items-center gap-2 mb-6">
                <MessageTextSquare01 className="h-5 w-5 text-tertiary" />
                <h2 className="text-lg font-semibold text-primary">
                    {t('detail.tabs.notes', 'Notes')}
                </h2>
            </div>

            {/* Tab Selector */}
            <div className="flex gap-1 mb-6 border-b border-secondary">
                <button
                    onClick={() => setActiveTab('internal')}
                    className={`flex items-center gap-2 px-3 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                        activeTab === 'internal'
                            ? 'border-brand-600 text-brand-600'
                            : 'border-transparent text-tertiary hover:text-secondary'
                    }`}
                >
                    <Lock01 className="h-4 w-4" />
                    <span>{t('detail.actions.internalNotes', 'Internal')}</span>
                    {notesData && notesData.internal_count > 0 && (
                        <span className="text-xs bg-secondary px-1.5 py-0.5 rounded-full">
                            {notesData.internal_count}
                        </span>
                    )}
                </button>
                <button
                    onClick={() => setActiveTab('customer')}
                    className={`flex items-center gap-2 px-3 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                        activeTab === 'customer'
                            ? 'border-brand-600 text-brand-600'
                            : 'border-transparent text-tertiary hover:text-secondary'
                    }`}
                >
                    <Users01 className="h-4 w-4" />
                    <span>{t('detail.actions.customerNotes', 'Customer')}</span>
                    {notesData && notesData.customer_count > 0 && (
                        <span className="text-xs bg-secondary px-1.5 py-0.5 rounded-full">
                            {notesData.customer_count}
                        </span>
                    )}
                </button>
                <button
                    onClick={() => setActiveTab('actions')}
                    className={`flex items-center gap-2 px-3 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                        activeTab === 'actions'
                            ? 'border-brand-600 text-brand-600'
                            : 'border-transparent text-tertiary hover:text-secondary'
                    }`}
                >
                    <CheckCircle className="h-4 w-4" />
                    <span>{t('detail.notes.actionsTab')}</span>
                    {totalActionsCount > 0 && (
                        <span className="text-xs bg-secondary px-1.5 py-0.5 rounded-full">
                            {totalActionsCount}
                        </span>
                    )}
                </button>
            </div>

            {/* Add Note Form (only for internal / customer tabs) */}
            {isNotesTab && (
                <div className="mb-6">
                    <TextArea
                        value={newNoteContent}
                        onChange={setNewNoteContent}
                        placeholder={
                            activeTab === 'internal'
                                ? t('detail.actions.internalNotePlaceholder', 'Add an internal note (only visible to your team)...')
                                : t('detail.actions.customerNotePlaceholder', 'Add a note for stakeholders...')
                        }
                        rows={3}
                    />
                    <div className="flex justify-end mt-3">
                        <Button
                            color="primary"
                            size="sm"
                            onClick={handleAddNote}
                            isLoading={createMutation.isPending}
                            iconLeading={Plus}
                        >
                            {t('detail.actions.addNote', 'Add Note')}
                        </Button>
                    </div>
                </div>
            )}

            {/* Content */}
            <div className="space-y-3">
                {/* Notes list (internal / customer) */}
                {isNotesTab && (
                    <>
                        {isLoading && (
                            <div className="flex items-center justify-center py-8">
                                <p className="text-tertiary">{t('detail.loading', 'Loading...')}</p>
                            </div>
                        )}

                        {!isLoading && currentNotes.length === 0 && (
                            <EmptyState type={activeTab} />
                        )}

                        {!isLoading && currentNotes.map((note) => (
                            <NoteItem
                                key={note.note_id}
                                note={note}
                                onDelete={handleDeleteNote}
                                isDeleting={deletingNoteId === note.note_id}
                            />
                        ))}
                    </>
                )}

                {/* Actions list */}
                {activeTab === 'actions' && (
                    <>
                        {activitiesLoading && (
                            <div className="flex items-center justify-center py-8">
                                <p className="text-tertiary">{t('detail.loading', 'Loading...')}</p>
                            </div>
                        )}

                        {!activitiesLoading && totalActionsCount === 0 && (
                            <EmptyState type="actions" />
                        )}

                        {/* Evaluation actions first */}
                        {!activitiesLoading && evaluationActions.map((activity) => (
                            <EvaluationActionItem
                                key={activity.activity_id}
                                activity={activity}
                                onViewResults={setViewResults}
                            />
                        ))}

                        {/* Issue actions */}
                        {!activitiesLoading && issueActions.map((activity) => (
                            <ActionItem key={activity.activity_id} activity={activity} />
                        ))}
                    </>
                )}
            </div>

            {/* View-only evaluation results modal */}
            <EvaluateDocumentsModal
                containerId={containerId}
                containerNumber={containerNumber}
                isOpen={viewResults !== null}
                onClose={() => setViewResults(null)}
                onSuccess={() => {}}
                viewMode
                initialResults={viewResults ?? undefined}
            />
        </div>
    );
}
